import { useEffect, useRef, useState, type JSX } from 'react';
import mermaid from 'mermaid';
import { CopyIcon, CheckIcon } from './icons.js';

/**
 * Renders an agent-authored ```mermaid fenced block as a diagram, with an
 * export toolbar so the user can take it OUT of the KB and edit it elsewhere —
 * the KB never flattens a diagram to a static image. draw.io imports Mermaid
 * natively (Arrange ▸ Insert ▸ Advanced ▸ Mermaid), so "Edit in draw.io" opens
 * draw.io and copies the source ready to paste; "Download SVG" gives an
 * editable vector for any tool.
 *
 * Wired in via CodeBlock (the shared react-markdown `code` override), so it
 * appears identically in chat answers and the canvas.
 */

let initialised = false;
function ensureInit(): void {
  if (initialised) return;
  mermaid.initialize({
    startOnLoad: false,
    // strict: agent-generated source is untrusted — no raw HTML in labels.
    securityLevel: 'strict',
    theme: 'default',
    fontFamily: 'Open Sans, Roboto, sans-serif',
    // Throw on parse errors instead of rendering mermaid's default "bomb" error
    // SVG, so our own error card (source + Edit in draw.io) is what shows.
    suppressErrorRendering: true,
  });
  initialised = true;
}

// Monotonic id source so concurrent renders don't collide. Date.now()/random
// are fine here (client-only, not in a workflow sandbox).
let renderSeq = 0;

// Best-effort repair for the most common model slip: multiple statements mashed
// onto one line (`flowchart TD subgraph "X" direction LR A[..]` → parse error).
// Applied ONLY after an as-is render fails, so valid diagrams are never touched;
// if the repaired source also fails we show the error with the original.
function repairMermaid(code: string): string {
  return (
    code
      // Type declaration then content on the same line → break after the direction.
      .replace(/^(\s*(?:flowchart|graph)\s+(?:TB|TD|BT|LR|RL))\s+(?=\S)/gim, '$1\n')
      // Structural keywords onto their own line.
      .replace(/[^\S\n]+(subgraph\b)/g, '\n$1')
      .replace(/[^\S\n]+(direction\s+(?:TB|TD|BT|LR|RL)\b)/g, '\n$1')
      // …and break after the direction token if a node was mashed onto it.
      .replace(/\b(direction\s+(?:TB|TD|BT|LR|RL))[^\S\n]+(?=[A-Za-z])/g, '$1\n')
      // `end` that closes a subgraph (whole word, not inside a label like [Backend]).
      .replace(/[^\S\n]+(end)(?=\s|$)(?![^[]*\])/g, '\n$1')
      // A node/edge that ends in a shape bracket followed by the start of the
      // next node — but NOT an edge operator (`] -->` must stay on one line).
      .replace(/([\]})])[^\S\n]+(?=[A-Za-z])/g, '$1\n')
  );
}

export function MermaidDiagram({ code }: { code: string }): JSX.Element {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'src' | 'drawio' | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    ensureInit();
    const id = `mmd-${renderSeq++}`;
    const source = code.trim();
    if (!source) {
      setSvg(null);
      setError(null);
      return;
    }
    void (async () => {
      // Try as-is; on a parse error, retry once with a repaired source (fixes
      // the common "statements mashed onto one line" model slip).
      for (const [i, src] of [source, repairMermaid(source)].entries()) {
        try {
          const { svg: out } = await mermaid.render(`${id}-${i}`, src);
          if (alive.current) {
            setSvg(out);
            setError(null);
          }
          return;
        } catch (err) {
          if (i === 1 && alive.current) {
            setSvg(null);
            setError(err instanceof Error ? err.message : 'Could not render this diagram.');
          }
        }
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [code]);

  const copy = async (what: 'src' | 'drawio'): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code.trim());
      setCopied(what);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard blocked — silent */
    }
  };

  const editInDrawio = async (): Promise<void> => {
    // draw.io imports Mermaid natively but has no reliable preload-from-URL for
    // it, so copy the source and open draw.io — the user pastes into
    // Arrange ▸ Insert ▸ Advanced ▸ Mermaid. Open first (must be inside the
    // click gesture or the popup is blocked), then copy.
    window.open('https://app.diagrams.net/', '_blank', 'noopener,noreferrer');
    await copy('drawio');
  };

  const downloadSvg = (): void => {
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.svg';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-nebula-border bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-nebula-border bg-nebula-tint px-3 py-1.5 text-[11px] font-medium text-nebula-fg-2">
        <span className="font-mono uppercase tracking-wider">diagram</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void editInDrawio()}
            title="Open draw.io, then paste (Arrange ▸ Insert ▸ Advanced ▸ Mermaid)"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-nebula-secondary/10"
          >
            {copied === 'drawio' ? <CheckIcon size={12} /> : null}
            <span>{copied === 'drawio' ? 'Copied — paste in draw.io' : 'Edit in draw.io'}</span>
          </button>
          <button
            type="button"
            onClick={downloadSvg}
            disabled={!svg}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-nebula-secondary/10 disabled:opacity-40"
          >
            <span>Download SVG</span>
          </button>
          <button
            type="button"
            onClick={() => void copy('src')}
            title="Copy the Mermaid source"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-nebula-secondary/10"
          >
            {copied === 'src' ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
            <span>{copied === 'src' ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>
      {svg ? (
        <div className="overflow-x-auto px-3 py-3 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full">
          {/* mermaid renders trusted SVG (securityLevel: 'strict' sanitises
              labels); no user HTML reaches this sink. */}
          <div dangerouslySetInnerHTML={{ __html: svg }} />
        </div>
      ) : error ? (
        <div className="px-3 py-3">
          <div className="mb-2 text-[12px] text-nebula-error">
            This diagram couldn’t be rendered ({error}). The source is below — you can still edit it
            in draw.io.
          </div>
          <pre className="overflow-x-auto rounded bg-nebula-gray-6 p-2 text-[12px] font-mono text-nebula-fg-1">
            {code.trim()}
          </pre>
        </div>
      ) : (
        <div className="px-3 py-3 text-[12px] italic text-nebula-fg-4">Rendering diagram…</div>
      )}
    </div>
  );
}
